import { supabase } from "../../../foundation/altzero_base/base/utils/supabaseClient";
import { Site } from "../types/siteTypes";

const SITES_DOCUMENTS_BUCKET = 'sitesDocuments';

export interface SiteDB {
  id: string;
  org_id: string;
  client_id: string;
  name: string | null;
  nmi: string | null;
  abn: string | null;
  acn: string | null;
  due_at: string | null;
  current_contract_expiry: string | null;
  current_meter_provider: string | null;
  current_retailer: string | null;
  dma_provider: string | null;
  retailer_comms: string | null;
  create_dma: boolean;
  request_meter_data: boolean;
  request_bills: boolean;
  acquire_ced: boolean;
  green_power: boolean;
  annual_kwh_peak: number | null;
  annual_kwh_offpeak: number | null;
  annual_kwh_shoulder: number | null;
  addr_street: string | null;
  addr_city: string | null;
  addr_state: string | null;
  addr_postcode: string | null;
  addr_country: string | null;
  document_file_paths: string[] | null;
  retailer_notes: string | null;
  peak_demand_kw: number | null;
  current_contract_rates: Record<string, unknown> | null;
  daily_charges: number | null;
  non_energy_charges: number | null;
  extn: Record<string, any>;
  created_at: string;
  // SF-sourced fields
  sf_site_id: string | null;
  dma_status: string | null;
  meter_install_date: string | null;
  drive_link: string | null;
  loa_expiry_date: string | null;
  shield_platinum: boolean | null;
  annual_kwh_total: number | null;
  is_embedded: boolean | null;
  market_status: string | null;
  // Joined data
  client?: {
    id: string;
    name: string;
  };
}

/**
 * Get current user ID
 */
const getCurrentUserId = async (): Promise<string | null> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    return user?.id || null;
  } catch (error) {
    console.error("Error getting user ID:", error);
    return null;
  }
};

/**
 * Upload site document to Supabase storage
 */
export const uploadSiteDocument = async (
  file: File,
  siteId: string,
  orgId: string
): Promise<{
  success: boolean;
  filePath?: string;
  error?: string;
}> => {
  try {
    const userId = await getCurrentUserId();
    if (!userId) {
      return { success: false, error: "User not authenticated" };
    }

    // Generate unique file path: sites/{orgId}/{siteId}/{timestamp}_{filename}
    const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'bin';
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(2, 9);
    const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const fileName = `${timestamp}_${randomId}_${sanitizedFileName}`;
    const filePath = `${orgId}/${siteId}/${fileName}`;

    // Upload to Supabase Storage
    const { data, error } = await supabase.storage
      .from(SITES_DOCUMENTS_BUCKET)
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (error) {
      console.error('❌ Site document upload error:', error);
      return {
        success: false,
        error: `Upload failed: ${error.message}`
      };
    }

    console.log('✅ Site document uploaded successfully:', filePath);

    return {
      success: true,
      filePath: data.path,
    };
  } catch (error) {
    console.error('❌ Site document upload error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Upload failed'
    };
  }
};

/**
 * Delete site document from Supabase storage
 */
export const deleteSiteDocument = async (
  filePath: string
): Promise<{
  success: boolean;
  error?: string;
}> => {
  try {
    const { error } = await supabase.storage
      .from(SITES_DOCUMENTS_BUCKET)
      .remove([filePath]);

    if (error) {
      console.error('❌ Site document delete error:', error);
      return {
        success: false,
        error: `Delete failed: ${error.message}`
      };
    }

    return { success: true };
  } catch (error) {
    console.error('❌ Site document delete error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Delete failed'
    };
  }
};

/**
 * Get public URL for site document
 */
export const getSiteDocumentUrl = (filePath: string): string => {
  const { data } = supabase.storage
    .from(SITES_DOCUMENTS_BUCKET)
    .getPublicUrl(filePath);
  return data.publicUrl;
};

/**
 * Get user's organization ID
 */
const getUserOrganizationId = async (): Promise<string | null> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data: orgMember, error } = await supabase
      .from("base_organisation_members")
      .select("organisation_id")
      .eq("user_id", user.id)
      .limit(1)
      .single();

    if (error) {
      console.error("Error getting org ID:", error);
      return null;
    }

    return orgMember?.organisation_id || null;
  } catch (error) {
    console.error("Error getting organization ID:", error);
    return null;
  }
};

/**
 * Get all sites for the user's organization
 */
export const getSites = async (): Promise<{
  success: boolean;
  data?: SiteDB[];
  error?: string;
}> => {
  try {
    const orgId = await getUserOrganizationId();
    if (!orgId) {
      return { success: false, error: "Organization not found" };
    }

    const { data, error } = await supabase
      .from("site")
      .select(`
        *,
        client:client_id (
          id,
          name
        )
      `)
      .eq("org_id", orgId)
      .order("created_at", { ascending: false });

    if (error) throw error;

    return { success: true, data: data || [] };
  } catch (error: any) {
    console.error("Error fetching sites:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get sites by client ID
 */
export const getSitesByClient = async (
  clientId: string
): Promise<{
  success: boolean;
  data?: SiteDB[];
  error?: string;
}> => {
  try {
    const orgId = await getUserOrganizationId();
    if (!orgId) {
      return { success: false, error: "Organization not found" };
    }

    const { data, error } = await supabase
      .from("site")
      .select(`
        *,
        client:client_id (
          id,
          name
        )
      `)
      .eq("org_id", orgId)
      .eq("client_id", clientId)
      .order("created_at", { ascending: false });

    if (error) throw error;

    return { success: true, data: data || [] };
  } catch (error: any) {
    console.error("Error fetching sites by client:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Create a new site
 */
export const createSite = async (data: {
  org_id: string;
  client_id: string;
  site: Site;
}): Promise<{
  success: boolean;
  data?: SiteDB;
  error?: string;
}> => {
  try {
    const { data: insertedData, error } = await supabase
      .from("site")
      .insert({
        org_id: data.org_id,
        client_id: data.client_id,
        name: data.site.name,
        nmi: data.site.nmi,
        abn: data.site.abn || null,
        acn: data.site.acn || null,
        due_at: data.site.dueAt ? new Date(data.site.dueAt).toISOString() : null,
        current_contract_expiry: data.site.currentContractExpiry ? new Date(data.site.currentContractExpiry).toISOString().split('T')[0] : null,
        current_meter_provider: data.site.currentMeterProvider || null,
        current_retailer: data.site.currentRetailer || null,
        dma_provider: data.site.dmaProvider || null,
        retailer_comms: data.site.retailerComms || null,
        create_dma: data.site.createDma || false,
        request_meter_data: data.site.requestMeterData || false,
        request_bills: data.site.requestBills || false,
        acquire_ced: data.site.acquireCed || false,
        green_power: data.site.greenPower || false,
        annual_kwh_peak: data.site.annualKwhPeak || null,
        annual_kwh_offpeak: data.site.annualKwhOffpeak || null,
        annual_kwh_shoulder: data.site.annualKwhShoulder || null,
        annual_kwh_total: data.site.annualKwhTotal || (() => {
          const peak = data.site.annualKwhPeak || 0;
          const offpeak = data.site.annualKwhOffpeak || 0;
          const shoulder = data.site.annualKwhShoulder || 0;
          const total = peak + offpeak + shoulder;
          return total > 0 ? total : null;
        })(),
        addr_street: data.site.address.street,
        addr_city: data.site.address.city,
        addr_state: data.site.address.state,
        addr_postcode: data.site.address.postcode,
        addr_country: data.site.address.country,
        document_file_paths: data.site.documentFilePaths || [],
        retailer_notes: data.site.retailerNotes || null,
        peak_demand_kw: data.site.peakDemandKw || 0,
        current_contract_rates: data.site.currentContractRates || {},
        daily_charges: data.site.dailyCharges || 0,
        non_energy_charges: data.site.nonEnergyCharges || 0,
        extn: {},
        created_at: new Date().toISOString(),
      })
      .select(`
        *,
        client:client_id (
          id,
          name
        )
      `)
      .single();

    if (error) throw error;

    return { success: true, data: insertedData };
  } catch (error: any) {
    console.error("Error creating site:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Update an existing site
 */
export const updateSite = async (
  id: string,
  site: Partial<Site>
): Promise<{
  success: boolean;
  data?: SiteDB;
  error?: string;
}> => {
  try {
    const updateData: any = {};

    if (site.name !== undefined) updateData.name = site.name;
    if (site.nmi !== undefined) updateData.nmi = site.nmi;
    if (site.abn !== undefined) updateData.abn = site.abn || null;
    if (site.acn !== undefined) updateData.acn = site.acn || null;
    if (site.dueAt !== undefined) updateData.due_at = site.dueAt ? new Date(site.dueAt).toISOString() : null;
    if (site.currentContractExpiry !== undefined) {
      updateData.current_contract_expiry = site.currentContractExpiry ? new Date(site.currentContractExpiry).toISOString().split('T')[0] : null;
    }
    // Legacy support for contractExpiry field name
    if ((site as any).contractExpiry !== undefined && site.currentContractExpiry === undefined) {
      updateData.current_contract_expiry = (site as any).contractExpiry ? new Date((site as any).contractExpiry).toISOString().split('T')[0] : null;
    }
    if (site.currentMeterProvider !== undefined) updateData.current_meter_provider = site.currentMeterProvider || null;
    if (site.currentRetailer !== undefined) updateData.current_retailer = site.currentRetailer || null;
    if (site.dmaProvider !== undefined) updateData.dma_provider = site.dmaProvider || null;
    if (site.retailerComms !== undefined) updateData.retailer_comms = site.retailerComms || null;
    // Boolean flags — handle both camelCase variants for backward compatibility
    if ((site as any).createDma !== undefined) updateData.create_dma = (site as any).createDma;
    else if ((site as any).createDMA !== undefined) updateData.create_dma = (site as any).createDMA;
    if (site.requestMeterData !== undefined) updateData.request_meter_data = site.requestMeterData;
    if (site.requestBills !== undefined) updateData.request_bills = site.requestBills;
    if ((site as any).acquireCed !== undefined) updateData.acquire_ced = (site as any).acquireCed;
    else if ((site as any).acquireCED !== undefined) updateData.acquire_ced = (site as any).acquireCED;
    if ((site as any).greenPower !== undefined) updateData.green_power = (site as any).greenPower;

    // Annual kWh — prefer direct fields (annualKwhPeak etc.) then legacy annualUsage structure
    if ((site as any).annualKwhPeak !== undefined) {
      updateData.annual_kwh_peak = (site as any).annualKwhPeak || null;
    } else if ((site as any).annualUsage?.peak !== undefined) {
      updateData.annual_kwh_peak = (site as any).annualUsage.peak || null;
    }
    if ((site as any).annualKwhOffpeak !== undefined) {
      updateData.annual_kwh_offpeak = (site as any).annualKwhOffpeak || null;
    } else if ((site as any).annualUsage?.offPeak !== undefined) {
      updateData.annual_kwh_offpeak = (site as any).annualUsage.offPeak || null;
    }
    if ((site as any).annualKwhShoulder !== undefined) {
      updateData.annual_kwh_shoulder = (site as any).annualKwhShoulder || null;
    } else if ((site as any).annualUsage?.shoulder !== undefined) {
      updateData.annual_kwh_shoulder = (site as any).annualUsage.shoulder || null;
    }
    if ((site as any).annualKwhTotal !== undefined) {
      updateData.annual_kwh_total = (site as any).annualKwhTotal || (() => {
        const p = (site as any).annualKwhPeak || 0;
        const o = (site as any).annualKwhOffpeak || 0;
        const s = (site as any).annualKwhShoulder || 0;
        return (p + o + s) > 0 ? p + o + s : null;
      })();
    }

    if (site.address) {
      if (site.address.street !== undefined) updateData.addr_street = site.address.street;
      if (site.address.city !== undefined) updateData.addr_city = site.address.city;
      if (site.address.state !== undefined) updateData.addr_state = site.address.state;
      if (site.address.postcode !== undefined) updateData.addr_postcode = site.address.postcode;
      if (site.address.country !== undefined) updateData.addr_country = site.address.country;
    }

    if (site.documentFilePaths !== undefined) {
      updateData.document_file_paths = site.documentFilePaths || [];
    }

    if (site.retailerNotes !== undefined) {
      updateData.retailer_notes = site.retailerNotes || null;
      console.log('📝 Updating retailer_notes:', { siteId: id, retailerNotes: site.retailerNotes || null });
    }

    if (site.peakDemandKw !== undefined) updateData.peak_demand_kw = site.peakDemandKw || 0;
    if (site.currentContractRates !== undefined) updateData.current_contract_rates = site.currentContractRates || {};
    if (site.dailyCharges !== undefined) updateData.daily_charges = site.dailyCharges || 0;
    if (site.nonEnergyCharges !== undefined) updateData.non_energy_charges = site.nonEnergyCharges || 0;

    // SF-sourced site fields
    if ((site as any).loaExpiryDate !== undefined) updateData.loa_expiry_date = (site as any).loaExpiryDate || null;
    // Shield Platinum only ever promotes. Every site in Bill Validation is a
    // Shield Platinum site (job 10), so a Salesforce Opportunity that reports
    // false must not silently strip the flag from a site the customer considers
    // enrolled — a refresh would otherwise undo the backfill a row at a time.
    if ((site as any).shieldPlatinum === true) updateData.shield_platinum = true;
    if ((site as any).dmaStatus !== undefined) updateData.dma_status = (site as any).dmaStatus || null;
    if ((site as any).meterInstallDate !== undefined) updateData.meter_install_date = (site as any).meterInstallDate || null;
    if ((site as any).sitedriveLink !== undefined) updateData.drive_link = (site as any).siteDriveLink || null;
    if ((site as any).isEmbedded !== undefined) updateData.is_embedded = (site as any).isEmbedded;
    if ((site as any).marketStatus !== undefined) updateData.market_status = (site as any).marketStatus || null;

    console.log('🔄 Update payload:', JSON.stringify(updateData, null, 2));

    const { data, error } = await supabase
      .from("site")
      .update(updateData)
      .eq("id", id)
      .select(`
        *,
        client:client_id (
          id,
          name
        )
      `)
      .single();

    if (error) throw error;

    return { success: true, data };
  } catch (error: any) {
    console.error("Error updating site:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete a site
 */
export const deleteSite = async (
  id: string
): Promise<{
  success: boolean;
  error?: string;
}> => {
  try {
    const { error } = await supabase.from("site").delete().eq("id", id);

    if (error) throw error;

    return { success: true };
  } catch (error: any) {
    console.error("Error deleting site:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get site by ID
 */
export const getSiteById = async (
  id: string
): Promise<{
  success: boolean;
  data?: SiteDB;
  error?: string;
}> => {
  try {
    const { data, error } = await supabase
      .from("site")
      .select(`
        *,
        client:client_id (
          id,
          name
        )
      `)
      .eq("id", id)
      .single();

    if (error) throw error;

    return { success: true, data };
  } catch (error: any) {
    console.error("Error fetching site:", error);
    return { success: false, error: error.message };
  }
};

